import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

type AppShellProps = {
  isModalActive: boolean;
  main: React.ReactNode;
  belowMain?: React.ReactNode;
  modalLayer?: React.ReactNode;
};

const getAnnouncement = (pathname: string) => {
  if (typeof document !== 'undefined') {
    const title = document.title?.trim();
    if (title) {
      return title;
    }
  }
  return `Navigated to ${pathname}`;
};

const AppShell: React.FC<AppShellProps> = ({
  isModalActive,
  main,
  belowMain,
  modalLayer,
}) => {
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const firstRenderRef = useRef(true);
  const [announcement, setAnnouncement] = useState(() =>
    getAnnouncement(location.pathname),
  );

  useEffect(() => {
    if (isModalActive) {
      return;
    }

    const nextAnnouncement = getAnnouncement(location.pathname);
    setAnnouncement(nextAnnouncement);
  }, [isModalActive, location.pathname]);

  useEffect(() => {
    if (isModalActive) {
      return undefined;
    }
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return undefined;
    }
    const mainEl = mainRef.current;
    if (!mainEl) {
      return undefined;
    }
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }
    if (document.activeElement === mainEl) {
      return undefined;
    }

    const focusMain = () => {
      mainEl.focus();
    };

    const handle =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(focusMain)
        : window.setTimeout(focusMain, 0);

    return () => {
      if (typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(handle as number);
      } else {
        window.clearTimeout(handle as number);
      }
    };
  }, [isModalActive, location.key]);

  const ariaLiveMessage = useMemo(
    () => announcement || 'Page updated',
    [announcement],
  );

  return (
    <>
      <a className="skip-to-content" href="#app-main">
        Skip to main content
      </a>
      <div className="app-shell">
        <main id="app-main" ref={mainRef} tabIndex={-1} role="main">
          {main}
        </main>
      </div>
      {belowMain}
      {modalLayer}
      <span
        className="visually-hidden"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {ariaLiveMessage}
      </span>
    </>
  );
};

export default AppShell;
